import React, { useState } from 'react';
import { Shield, ChevronDown, ChevronUp, AlertTriangle } from 'lucide-react';
interface AgreementProps {
  agreed: boolean;
  onUpdate: (field: string, value: string) => void;
}
const AGREEMENT_SECTIONS = [{
  title: '1. Equipment Use',
  content: `The Andros Ultra Binder ("the Equipment") is provided by Southern Downs Regional Council ("the Council") for the sole purpose of agricultural plastic waste recovery. The Borrower agrees to operate the Equipment only for its intended purpose, in accordance with the manufacturer's guidelines and any instructions provided by the Council or its representatives.`
}, {
  title: '2. Booking Period & Return',
  content: `The Borrower must return the Equipment by the agreed end date of their booking or arrange a confirmed handover to the next booked farmer. Failure to return or hand over the Equipment on time may result in suspension from the booking system.`
}, {
  title: '3. Care & Maintenance',
  content: `While using the Equipment, the Borrower is responsible for the reasonable care and maintenance of the Equipment, including:\n\n• Performing basic operational checks before each use\n• Storing the Equipment in a secure, weather-protected location when not in use\n• Not modifying, altering, or tampering with the Equipment in any way\n• Cleaning the Equipment to remove soil and plant matter is required before returning it to the next farmer or the Stanthorpe WMF (for biosecurity purposes)\n\nThe Equipment must be returned in the same working condition it was received.`
}, {
  title: '4. Damage & Loss',
  content: `The Borrower must report any damage, malfunction, or loss by contacting Natasha Frazer from RMCG on 0451 982 442 or agplastic.SDRC@gmail.com. The Borrower may be liable for the cost of repairs or replacement if damage results from misuse, negligence, or failure to follow operating instructions.`
}, {
  title: '5. Transport',
  content: `The Borrower is responsible for the safe transport of the Equipment to and from their property, whether collecting from the Stanthorpe WMF or from another farmer's property. The Borrower must ensure they have an appropriate vehicle for safe transport.`
}, {
  title: '6. Liability',
  content: `The Council accepts no liability for any injury, loss, or damage arising from the transport or use of the Equipment.\n\nYou will indemnify and keep indemnified, Council and RMCG against any claims, costs, demands and suits arising out of or in connection with the misuse of the Equipment.`
}];
export function Agreement({
  agreed,
  onUpdate
}: AgreementProps) {
  const [expandedSection, setExpandedSection] = useState<number | null>(null);
  const [hasScrolledTerms, setHasScrolledTerms] = useState(false);
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const target = e.target as HTMLDivElement;
    const isNearBottom = target.scrollHeight - target.scrollTop - target.clientHeight < 40;
    if (isNearBottom) {
      setHasScrolledTerms(true);
    }
  };
  const toggleSection = (index: number) => {
    setExpandedSection(expandedSection === index ? null : index);
  };
  return <div className="space-y-5" data-id="element-422">
      <div className="mb-4" data-id="element-423">
        <h3 className="text-lg font-bold text-slate-900 mb-2" data-id="element-424">
          User Agreement & Liability Waiver
        </h3>
        <p className="text-sm text-slate-500" data-id="element-425">
          Please read and accept the terms before confirming your booking.
        </p>
      </div>

      {/* Agreement Header */}
      <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3" data-id="element-426">
        <AlertTriangle size={20} className="text-amber-600 shrink-0 mt-0.5" data-id="element-427" />
        <div className="text-sm text-amber-800" data-id="element-428">
          <p className="font-semibold" data-id="element-429">Important — Please read carefully</p>
          <p className="mt-1 text-amber-700" data-id="element-430">
            By accepting this agreement, you acknowledge responsibility for the
            Andros Ultra Binder during your booking period.
          </p>
        </div>
      </div>

      {/* Scrollable Terms */}
      <div className="border border-slate-200 rounded-xl overflow-hidden" data-id="element-431">
        <div className="bg-slate-50 px-4 py-3 border-b border-slate-200 flex items-center gap-2" data-id="element-432">
          <Shield size={16} className="text-slate-500" data-id="element-433" />
          <span className="text-sm font-semibold text-slate-700" data-id="element-434">
            Equipment Loan Agreement — Southern Downs Regional Council
          </span>
        </div>

        <div className="max-h-[280px] overflow-y-auto divide-y divide-slate-100" onScroll={handleScroll} data-id="element-435">
          {AGREEMENT_SECTIONS.map((section, index) => <div key={index} className="bg-white" data-id="element-436">
              <button type="button" onClick={() => toggleSection(index)} className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-slate-50 transition-colors" data-id="element-437">
                <span className="text-sm font-medium text-slate-800" data-id="element-438">
                  {section.title}
                </span>
                {expandedSection === index ? <ChevronUp size={16} className="text-slate-400 shrink-0" data-id="element-439" /> : <ChevronDown size={16} className="text-slate-400 shrink-0" data-id="element-440" />}
              </button>
              {expandedSection === index && <div className="px-4 pb-4 text-sm text-slate-600 leading-relaxed whitespace-pre-line" data-id="element-441">
                  {section.content}
                </div>}
            </div>)}
        </div>
      </div>

      {/* Agreement Checkbox */}
      <label className={`
          flex items-start gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all
          ${agreed ? 'border-emerald-500 bg-emerald-50' : 'border-slate-200 bg-white hover:border-slate-300'}
        `} data-id="element-442">
        <input type="checkbox" checked={agreed} onChange={e => onUpdate('agreementAccepted', e.target.checked ? 'true' : 'false')} className="mt-0.5 h-5 w-5 rounded border-slate-300 text-emerald-600 focus:ring-emerald-500 cursor-pointer" data-id="element-443" />
        <div className="text-sm" data-id="element-444">
          <p className={`font-semibold ${agreed ? 'text-emerald-900' : 'text-slate-900'}`} data-id="element-445">
            I have read and agree to the Equipment Loan Agreement
          </p>
          <p className={`mt-1 ${agreed ? 'text-emerald-700' : 'text-slate-500'}`} data-id="element-446">
            I accept full responsibility for the Andros Ultra Binder during my
            booking period, including care, transport, and return of the
            equipment in good working condition.
          </p>
        </div>
      </label>
    </div>;
}